import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { clearableIsDisabled, clearableIsReadOnly } from './clearable-field.type';
import { FieldLabelRowComponent } from './field-label-row.component';

interface MergeFieldOption {
  value: string;
  label: string;
  type?: string;
}

/**
 * Custom Formly field type that renders a textarea with a "merge fields" picker button.
 * The button opens a menu with available source fields. Selecting a field inserts the
 * `{keyPath}` placeholder at the cursor position (or appends if no cursor).
 *
 * Used internally by the form-builder for configuring linked-value templates.
 */
@Component({
  selector: 'formly-field-template-merge-input',
  template: `
    <!--
      Width classes from field.className are already applied by formly to the host
      <formly-field>. Re-applying them here compounded them (a half-width field rendered at
      50% of 50%); the inner field just fills its host.
    -->
    <mat-form-field class="w-full">
      <mat-label>
        <app-field-label-row [label]="props.label" [required]="props.required"></app-field-label-row>
      </mat-label>
      <textarea
        #templateInput
        matInput
        [formControl]="formControl"
        [formlyAttributes]="field"
        [formlyDataCy]="field"
        [rows]="props.rows || 5"
        [readonly]="isReadOnly()"
        [placeholder]="props.placeholder || ''">
      </textarea>
      <span matSuffix class="inline-flex items-center">
        <button
          mat-icon-button
          type="button"
          [matMenuTriggerFor]="mergeFieldsMenu"
          [disabled]="!hasOptions() || isDisabled() || isReadOnly()"
          matTooltip="Merge field invoegen"
          (click)="$event.stopPropagation()">
          <mat-icon svgIcon="feather:plus-circle" class="icon-size-4" />
        </button>
      </span>
      <mat-menu #mergeFieldsMenu="matMenu" class="max-h-96 overflow-auto">
        @for (option of mergeFieldOptions(); track option.value) {
          <button
            mat-menu-item
            type="button"
            (click)="insertMergeField(option.value, templateInput)">
            <span class="font-medium">{{ option.label }}</span>
            <span class="text-hint text-sm ml-2">{{ '{' + option.value + '}' }}</span>
          </button>
        } @empty {
          <span mat-menu-item disabled>Geen velden beschikbaar — selecteer eerst een bronveld</span>
        }
      </mat-menu>
      @if (props.description) {
        <mat-hint>{{ props.description }}</mat-hint>
      }
    </mat-form-field>
    <app-formly-error [field]="field" />
  `,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatSuffix,
    MatInput,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    FieldLabelRowComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyTemplateMergeInputComponent extends FieldType<any> {
  isDisabled(): boolean {
    return clearableIsDisabled(this.formControl, this.props);
  }

  isReadOnly(): boolean {
    return clearableIsReadOnly(this.props);
  }

  mergeFieldOptions(): MergeFieldOption[] {
    const options = this.props?.options;
    return Array.isArray(options) ? (options as MergeFieldOption[]) : [];
  }

  hasOptions(): boolean {
    return this.mergeFieldOptions().length > 0;
  }

  insertMergeField(keyPath: string, textarea: HTMLTextAreaElement): void {
    if (this.isDisabled() || this.isReadOnly()) {
      return;
    }

    const placeholder = `{${keyPath}}`;
    const current = (this.formControl.value as string) || '';
    const cursorStart = textarea.selectionStart ?? current.length;
    const cursorEnd = textarea.selectionEnd ?? current.length;

    const newValue = current.substring(0, cursorStart) + placeholder + current.substring(cursorEnd);
    this.formControl.setValue(newValue);
    this.formControl.markAsDirty();

    // Restore cursor position after the inserted placeholder
    setTimeout(() => {
      const newCursorPos = cursorStart + placeholder.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }
}
